import React, { useState, useEffect } from 'react';
import { FaPhone, FaCheckCircle, FaExclamationTriangle, FaGlobe } from 'react-icons/fa';

const PhoneInput = ({ 
  value, 
  onChange, 
  placeholder = "Enter phone number", 
  className = "", 
  disabled = false,
  showValidation = true,
  onValidationChange = null 
}) => {
  const [isValid, setIsValid] = useState(false);
  const [validationMessage, setValidationMessage] = useState("");
  const [showCountrySelector, setShowCountrySelector] = useState(false);

  const countries = [
    { code: 'KE', name: 'Kenya', flag: '🇰🇪', prefix: '+254', pattern: /^\+254[17]\d{8}$/ },
    { code: 'SO', name: 'Somalia', flag: '🇸🇴', prefix: '+252', pattern: /^\+252[67]\d{8}$/ },
    { code: 'UG', name: 'Uganda', flag: '🇺🇬', prefix: '+256', pattern: /^\+256\d{9}$/ },
    { code: 'TZ', name: 'Tanzania', flag: '🇹🇿', prefix: '+255', pattern: /^\+255\d{9}$/ },
    { code: 'RW', name: 'Rwanda', flag: '🇷🇼', prefix: '+250', pattern: /^\+250\d{9}$/ },
  ];

  const [selectedCountry, setSelectedCountry] = useState(countries[0]);

  useEffect(() => {
    validatePhone(value);
  }, [value, selectedCountry]);

  const validatePhone = (phoneNumber) => {
    if (!phoneNumber) {
      setIsValid(false);
      setValidationMessage("");
      onValidationChange?.({ isValid: false, message: "" });
      return;
    }

    // Check if it matches the selected country pattern
    if (selectedCountry.pattern.test(phoneNumber)) {
      setIsValid(true);
      setValidationMessage(`✓ Valid ${selectedCountry.name} number`);
      onValidationChange?.({ isValid: true, message: `✓ Valid ${selectedCountry.name} number` });
      return;
    }

    // Check if it's a valid international format
    if (/^\+[1-9]\d{1,14}$/.test(phoneNumber)) {
      setIsValid(true);
      setValidationMessage("✓ Valid international number");
      onValidationChange?.({ isValid: true, message: "✓ Valid international number" });
      return;
    }

    setIsValid(false);
    setValidationMessage("Please enter a valid phone number");
    onValidationChange?.({ isValid: false, message: "Please enter a valid phone number" });
  };

  const handleCountrySelect = (country) => {
    setSelectedCountry(country);
    setShowCountrySelector(false);
    
    // If user has already entered a number, update it with new country prefix
    if (value && !value.startsWith(country.prefix)) {
      const numberWithoutPrefix = value.replace(/^\+\d+/, '');
      const newNumber = country.prefix + numberWithoutPrefix;
      onChange(newNumber);
    }
  };

  const formatPhoneNumber = (input) => {
    // Remove all non-digit characters except +
    let cleaned = input.replace(/[^\d+]/g, '');
    
    // Ensure it starts with +
    if (!cleaned.startsWith('+')) {
      cleaned = '+' + cleaned;
    }
    
    // Limit to reasonable length
    if (cleaned.length > 16) {
      cleaned = cleaned.slice(0, 16);
    }
    
    return cleaned;
  };

  const handleInputChange = (e) => {
    const formatted = formatPhoneNumber(e.target.value);
    onChange(formatted);
  };

  const handleInputFocus = () => {
    setShowCountrySelector(false);
  };

  return (
    <div className="relative">
      {/* Country Selector */}
      <div className="relative mb-2">
        <button
          type="button"
          onClick={() => setShowCountrySelector(!showCountrySelector)}
          className="flex items-center gap-2 px-3 py-2 text-sm bg-gray-100 dark:bg-navy-800 border border-gray-300 dark:border-navy-600 rounded-lg hover:bg-gray-200 dark:hover:bg-navy-700 transition-colors"
        >
          <span className="text-lg">{selectedCountry.flag}</span>
          <span className="text-gray-700 dark:text-gray-300">{selectedCountry.code}</span>
          <FaGlobe className="text-gray-500" />
        </button>
        
        {showCountrySelector && (
          <div className="absolute top-full left-0 mt-1 w-48 bg-white dark:bg-navy-800 border border-gray-200 dark:border-navy-600 rounded-lg shadow-lg z-10">
            {countries.map((country) => (
              <button
                key={country.code}
                onClick={() => handleCountrySelect(country)}
                className="w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-gray-50 dark:hover:bg-navy-700 transition-colors first:rounded-t-lg last:rounded-b-lg"
              >
                <span className="text-lg">{country.flag}</span>
                <div>
                  <div className="font-medium text-gray-700 dark:text-gray-300">{country.name}</div>
                  <div className="text-sm text-gray-500 dark:text-gray-400">{country.prefix}</div>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Phone Input */}
      <div className="relative">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <FaPhone className="h-5 w-5 text-gray-400" />
        </div>
        
        <input
          type="tel"
          value={value}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          placeholder={placeholder}
          disabled={disabled}
          className={`w-full h-14 pl-10 pr-12 rounded-xl border-2 text-base text-navy-700 dark:text-white transition-all duration-200 ${
            isValid
              ? 'border-green-500 bg-green-50 dark:bg-green-900/20'
              : value
              ? 'border-red-500 bg-red-50 dark:bg-red-900/20'
              : 'border-gray-200 dark:border-navy-700 dark:bg-navy-900'
          } focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20 disabled:bg-gray-100 disabled:cursor-not-allowed ${className}`}
        />
        
        {/* Validation Icon */}
        {value && (
          <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
            {isValid ? (
              <FaCheckCircle className="h-5 w-5 text-green-500" />
            ) : (
              <FaExclamationTriangle className="h-5 w-5 text-red-500" />
            )}
          </div>
        )}
      </div>
      
      {/* Validation Message */}
      {showValidation && value && (
        <p className={`text-sm mt-2 ${
          isValid 
            ? 'text-green-600 dark:text-green-400' 
            : 'text-red-600 dark:text-red-400'
        }`}>
          {validationMessage}
        </p>
      )}
      
      {/* Help Text */}
      <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
        Format: {selectedCountry.prefix} followed by your phone number
      </p>
    </div>
  );
};

export default PhoneInput; 